/**
 * API client for the Pathfinder Onboarding backend.
 * In development, CRA's proxy forwards /api to localhost:3001.
 * In production, set REACT_APP_API_URL to the backend URL.
 */

import { getIdToken } from "../auth/authConfig";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function request(method, path, body = null) {
  const options = {
    method,
    headers: { "Content-Type": "application/json" },
  };

  const token = await getIdToken();
  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, options);

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    const error = new Error(err.error || `Request failed: ${response.status}`);
    error.status = response.status;
    if (err.hint) error.hint = err.hint;
    throw error;
  }

  return response.json();
}

// ─── Invitations ────────────────────────────────────────────────────────────

export async function createInvitation(data) {
  return request("POST", "/api/invitations", data);
}

export async function getInvitationDetails(token) {
  return request("GET", `/api/invitations/${token}`);
}

export async function listInvitations(status = null) {
  const qs = status ? `?status=${status}` : "";
  return request("GET", `/api/invitations${qs}`);
}

export async function cancelInvitation(id) {
  return request("DELETE", `/api/invitations/${id}/cancel`);
}

export async function sendInvitationEmail(id) {
  return request("POST", `/api/invitations/${id}/send-email`);
}

// ─── Auth / Consent ─────────────────────────────────────────────────────────

export function getConsentUrl(token) {
  return `${API_BASE}/api/auth/consent?token=${encodeURIComponent(token)}`;
}

// ─── Clients ────────────────────────────────────────────────────────────────

export async function listClients(status = null) {
  const qs = status ? `?status=${status}` : "";
  return request("GET", `/api/clients${qs}`);
}

export async function getClient(id) {
  return request("GET", `/api/clients/${id}`);
}

export async function revokeClient(id) {
  return request("POST", `/api/clients/${id}/revoke`);
}

export async function regenerateInvitation(id) {
  return request("POST", `/api/clients/${id}/regenerate-invitation`);
}

export async function retryConfiguration(id) {
  return request("POST", `/api/clients/${id}/retry-configuration`);
}

export async function getTenantHealth(id, { refresh = false } = {}) {
  return request("GET", `/api/clients/${id}/health${refresh ? "?refresh=true" : ""}`);
}

// ─── Tenant Management ──────────────────────────────────────────────────────

export async function getClientLicenses(id) {
  return request("GET", `/api/clients/${id}/licenses`);
}

export async function assignClientLicense(id, payload) {
  return request("POST", `/api/clients/${id}/licenses/assign`, payload);
}

export async function getClientRoles(id) {
  return request("GET", `/api/clients/${id}/roles`);
}

export async function assignClientRole(id, payload) {
  return request("POST", `/api/clients/${id}/roles/assign`, payload);
}

export async function removeClientRole(id, assignmentId) {
  return request("DELETE", `/api/clients/${id}/roles/${assignmentId}`);
}

export async function getClientSignInLogs(id, params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return request("GET", `/api/clients/${id}/logs/signin${qs ? `?${qs}` : ""}`);
}

export async function getClientAuditLogs(id, params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return request("GET", `/api/clients/${id}/logs/audit${qs ? `?${qs}` : ""}`);
}

export async function getClientMailTrace(id, params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
  return request("GET", `/api/clients/${id}/mail/trace${qs ? `?${qs}` : ""}`);
}

// ─── Users & Groups admin ───────────────────────────────────────────────────

export async function listClientTenantUsers(id) {
  return request("GET", `/api/clients/${id}/users`);
}
export async function createClientUser(id, payload) {
  return request("POST", `/api/clients/${id}/users`, payload);
}
export async function updateClientUser(id, userId, payload) {
  return request("PATCH", `/api/clients/${id}/users/${userId}`, payload);
}
export async function deleteClientUser(id, userId) {
  return request("DELETE", `/api/clients/${id}/users/${userId}`);
}
export async function resetClientUserPassword(id, userId, payload) {
  return request("POST", `/api/clients/${id}/users/${userId}/reset-password`, payload);
}

export async function listClientGroups(id) {
  return request("GET", `/api/clients/${id}/groups`);
}
export async function createClientGroup(id, payload) {
  return request("POST", `/api/clients/${id}/groups`, payload);
}
export async function deleteClientGroup(id, groupId) {
  return request("DELETE", `/api/clients/${id}/groups/${groupId}`);
}
export async function listClientGroupMembers(id, groupId) {
  return request("GET", `/api/clients/${id}/groups/${groupId}/members`);
}
export async function addClientGroupMember(id, groupId, userId) {
  return request("POST", `/api/clients/${id}/groups/${groupId}/members`, { userId });
}
export async function removeClientGroupMember(id, groupId, userId) {
  return request("DELETE", `/api/clients/${id}/groups/${groupId}/members/${userId}`);
}

export async function deleteClient(id) {
  return request("DELETE", `/api/clients/${id}`);
}

export async function generateOffboardingLink(id) {
  return request("POST", `/api/clients/${id}/generate-offboarding`);
}

// ─── Off-boarding (public) ──────────────────────────────────────────────────

export async function getOffboardingDetails(token) {
  return request("GET", `/api/offboarding/${token}`);
}

export async function sendOffboardingEmail(id) {
  return request("POST", `/api/offboarding/${id}/send-email`);
}

export function getOffboardingConsentUrl(token) {
  return `${API_BASE}/api/auth/offboarding-consent?token=${encodeURIComponent(token)}`;
}

// ─── Audit ──────────────────────────────────────────────────────────────────

export async function getAuditLog(params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null)
  ).toString();
  return request("GET", `/api/audit${qs ? `?${qs}` : ""}`);
}

// ─── Users ──────────────────────────────────────────────────────────────────

export async function listPathfinderUsers() {
  return request("GET", "/api/users");
}

// ─── Roles ──────────────────────────────────────────────────────────────────

export async function listAvailableRoles() {
  return request("GET", "/api/roles");
}

// ─── Templates ──────────────────────────────────────────────────────────────

export async function listTemplates() {
  return request("GET", "/api/templates");
}

export async function getTemplate(id) {
  return request("GET", `/api/templates/${id}`);
}

// ─── Health ─────────────────────────────────────────────────────────────────

export async function healthCheck() {
  return request("GET", "/api/health");
}
